<script>
import { BTooltip } from "bootstrap-vue";
import { mapGetters } from "vuex";

import EmailCreatedSuccess from "./EmailCreatedSuccess.vue";
import SendDetails from "../Utility/SendDetails";
import PasswordActions from "../Utility/PasswordActions";
import GeneratePassword from "../Utility/GeneratePassword";
import { isNeoBrand, isAppLoadedInsideIframe } from "@/helpers";
import { clearLaunchEmailCreation } from "@/helpers/emailAccount.helper";
import BuyEmailAccountsFlow from "@/mixins/BuyEmailAccountsFlow";
import { toGB, isSizeInGB } from "../../helpers";
import brand from "../../helpers/brand";
import { REGX, EMAIL_VALIDITY_ERROR_CODES } from "../../helpers/const";
import viewTypes from "../../views";
import eventBus from "@/helpers/event-bus";
import { emailAvailabilityStatus, getUserNameFromEmail, validatePassword } from "@/helpers/email";
import emailService from "@/services/email/email.service";
import pendingAccountsService from "@/services/pending-accounts/pending-accounts.service";
import Logger from "@/helpers/logger";
import {
  trackNewEmailCreationViewed,
  trackEmailCreationCancelled,
  trackMailAccountCreated,
  trackSuccessViewed,
  trackSuccessDismissed,
  getPendingMailboxCount,
} from "@/telemetry/mailboxNudgeEvents";

export default {
  name: "CreateEmail",
  components: {
    SendDetails,
    PasswordActions,
    GeneratePassword,
    BTooltip,
    EmailCreatedSuccess,
  },
  mixins: [BuyEmailAccountsFlow],
  props: {
    hasAdmin: Boolean,
    view: Object,
    emailToDeleteDetails: {
      type: Object,
      default: () => ({
        accountId: false,
        emailId: "",
      }),
    },
    /**
     * Pre-fills the username input. Used by the success-screen nudge and the
     * Pending accounts screen when they hand off into the Create flow.
     * Falls back to the smartStorage launchEmailCreation value (existing
     * auto-fill mechanism) and finally to empty.
     */
    userName: {
      type: String,
      default: "",
    },
    /**
     * First-mailbox landing experience (iFrame): swaps the title to
     * "Create your first email on @{domain}" and, in iframe view, shows a
     * "Skip and go to control panel" secondary action.
     */
    isFirstCreation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const brandDetails = brand.getDetails();
    const launchValue = smartStorage.getItem("launchEmailCreation");
    const launchUsername = launchValue && launchValue !== "true" ? launchValue.trim() : "";
    // Analytics source_hook for this create flow. Launchers
    // (banner, pending screen, listing button) set `launchEmailCreationSource`
    // alongside `launchEmailCreation`; read-once here. With no stored source,
    // a first-mailbox lead-in maps to the upfront-creation screen and any
    // other direct open counts as the tab itself.
    const storedSource = smartStorage.getItem("launchEmailCreationSource");
    smartStorage.removeItem("launchEmailCreationSource");
    return {
      user: this.userName || launchUsername || "",
      email: "",
      altEmail: "",
      password: "",
      recipient: "",
      serverError: "",
      isAdmin: !this.hasAdmin,
      autoGenerate: false,
      showPassword: false,
      showAltEmailError: "",
      success: false,
      // Local mirror of the `isFirstCreation` prop. The success-screen nudge
      // re-primes this same (still-mounted) component for the next create,
      // at which point it's no longer the first mailbox — so we flip this
      // off in `primeForNextCreate` to drop the "first email" heading and
      // the "Skip and go to control panel" action. Kept separate from the
      // prop because a child can't mutate a prop.
      firstCreationActive: this.isFirstCreation,
      navigator: navigator,
      storageLimit: "",
      memorySizeUnit: "MB",
      theme: document.documentElement.getAttribute("theme"),
      hideBrandInfoInTooltip: false,
      showPasswordError: false,
      brandDetails,
      preventAccountCreation: false,
      emailAvailabilityError: "",
      deletedEmail: this.emailToDeleteDetails?.emailId, // Adding this to data instead of using directly from props because after the email is deleted and created successfully, the user object will get updated and won't have the info of the deleted email. We need to show the email that was deleted in a popup.
      creatingEmail: false,
      // Analytics: where this create flow was launched from. Persists across
      // nudge re-primes (`primeForNextCreate`) so chained creates keep the
      // source of the flow that started them.
      creationSourceHook:
        storedSource ||
        (this.isFirstCreation
          ? "first_account_upfront_creation_screen"
          : "create_email_account_tab"),
      // True only on the auto-redirect first-mailbox lead-in (cpWidget sets
      // this exact source as it bounces a fresh CP into Create). That landing
      // is a dead end, so its FORM hides the breadcrumb. Reaching the same
      // first-create screen any other way (e.g. clicking the Email accounts
      // card from Home) leaves this false — there's a Home to go back to, so
      // the breadcrumb shows. Read from the raw stored source, not
      // creationSourceHook, whose isFirstCreation fallback would also be the
      // upfront value and mask the distinction.
      isUpfrontFirstLanding: storedSource === "first_account_upfront_creation_screen",
      // Analytics: snapshot of the pre-filled username for viewed/cancelled
      // events (sent only when non-empty).
      prefilledUsername: this.userName || launchUsername || "",
    };
  },
  computed: {
    ...mapGetters(["accountIdByEmail"]),
    ...mapGetters({
      bundleId: "bundle/bundleId",
      domainName: "domain/domainName",
      currentPlanMailDetails: "mail/currentProductDetails",
      mailOrderId: "mail/orderId",
      availableLicenses: "mail/availableLicenses",
    }),
    isEmailCreationDisabled() {
      return (
        this.user.length < 1 ||
        this.showPasswordError ||
        !this.password.length ||
        this.creatingEmail
      );
    },
    /**
     * Title shown above the form. First-mailbox landing uses the
     * "Create your first email on @{domain}" copy.
     */
    headerLabel() {
      if (this.firstCreationActive) {
        return this.$t("emailAccounts.createEmail.firstMailboxHeading", {
          domain: this.domainName,
        });
      }
      return this.$t("emailAccounts.createEmail.heading");
    },
    /**
     * Skip button is iFrame-only — "Skip and go to control
     * panel" lands the user on cpWidget cards, which only exists in iframe
     * view. Standalone has no cards homescreen to skip to.
     *
     * Gated on `isUpfrontFirstLanding` (the auto-redirect lead-in) so it only
     * shows on that dead-end landing. Reaching first-create any other way —
     * e.g. clicking the Email-accounts tile card from Home, which sets no
     * source — leaves this false, so the regular Cancel shows instead of
     * Skip (MWDC-6352).
     */
    showSkipAction() {
      return (
        this.firstCreationActive && !this.success && this.isInIframe && this.isUpfrontFirstLanding
      );
    },
    /**
     * Cancel dismisses the Create form and returns to the Email accounts
     * listing. Shown in both standalone and iframe — but not alongside the
     * first-mailbox "Skip and go to control panel" action, which already
     * serves as that screen's exit. So in the iframe this surfaces on any
     * non-first creation (e.g. adding another mailbox from the success
     * nudge), where there'd otherwise be no way out of the form.
     */
    showCancelAction() {
      return !this.success && !this.showSkipAction;
    },
    /**
     * Breadcrumb back-nav is iframe-only — standalone has its own sidebar
     * chrome for navigation.
     */
    isInIframe() {
      return isAppLoadedInsideIframe();
    },
    /** Partner-provided documentation URL for the top-right Help link. */
    helpUrl() {
      const info = this.$store.getters.partnerInfo || {};
      return info.urls?.documentation || info.documentationUrl || "";
    },
  },
  watch: {
    password(newPassword) {
      if (this.autoGenerate) {
        this.showPasswordError = false;
        return;
      }
      this.validatePassword(newPassword);
    },
  },
  methods: {
    validatePassword(password) {
      // Empty = untouched (e.g. after `primeForNextCreate` resets the form
      // for a nudge-driven create). Don't flag it inline — the async
      // password watcher would otherwise surface "invalid password" before
      // the user types anything. Submission stays blocked regardless via
      // `isEmailCreationDisabled` (`!this.password.length`).
      if (!password) {
        this.showPasswordError = false;
        return;
      }
      const { isSmallPassword, isLargePassword, isPatternMatching } = validatePassword(password);
      if (isSmallPassword || isLargePassword || !isPatternMatching) {
        this.showPasswordError = true;
      } else {
        this.showPasswordError = false;
      }
    },
    close() {
      this.user = "";
      this.email = "";
      this.altEmail = "";
      this.password = "";
      this.recipient = "";
      this.serverError = "";
      this.autoGenerate = false;
      this.showPassword = false;
      this.success = false;
      this.$emit("close");
      clearLaunchEmailCreation();
    },
    /**
     * Cancel button on the form. Instrumented here (not in `close()`,
     * which is also invoked by non-cancel paths like the nudge Buy/Review
     * handoffs and the success view's own close).
     */
    onCancelClick() {
      trackEmailCreationCancelled({
        action: "cancel",
        sourceHook: this.creationSourceHook,
        prefilledUsername: this.prefilledUsername || undefined,
      });
      this.close();
    },
    /**
     * Iframe breadcrumb back-nav — dismisses whichever view is showing:
     * the form (a cancel) or the success screen (a dismissal).
     */
    onBreadcrumbNav() {
      if (this.success) {
        trackSuccessDismissed({ sourceHook: "breadcrumb" });
      } else {
        trackEmailCreationCancelled({
          action: "breadcrumb",
          sourceHook: this.creationSourceHook,
          prefilledUsername: this.prefilledUsername || undefined,
        });
      }
      this.close();
    },
    /**
     * "Skip and go to control panel" — secondary action on the
     * first-mailbox Create screen (iFrame only). Sets a session flag so
     * cpWidget doesn't immediately redirect back to /email_accounts, then
     * navigates to the cards homescreen.
     */
    skipFirstMailbox() {
      trackEmailCreationCancelled({
        action: "skip and go to control panel",
        sourceHook: this.creationSourceHook,
        prefilledUsername: this.prefilledUsername || undefined,
      });
      this.$store.dispatch("mail/mailboxNudge/markFirstMailboxSkipped");
      this.$emit("skip");
      this.close();
      this.$router.push({ path: "/home" });
    },
    /**
     * Reset the modal back to the Create form pre-primed with the given
     * username. Used by the success-screen multi-create loop — after the
     * first mailbox of N is created, the success screen surfaces the
     * remaining items and clicking the CTA primes the next one here.
     */
    primeForNextCreate(username) {
      // Past the first mailbox now — drop first-creation copy/skip action.
      this.firstCreationActive = false;
      this.user = username || "";
      this.email = "";
      this.altEmail = "";
      this.password = "";
      this.recipient = "";
      this.serverError = "";
      this.autoGenerate = false;
      this.showPassword = false;
      this.showPasswordError = false;
      this.success = false;
      this.creatingEmail = false;
      this.deletedEmail = "";
      // Recompute the admin default against the CURRENT admin state instead of
      // carrying over the first-creation value. `hasAdmin` is refreshed by the
      // post-create email-list refetch, so once the first mailbox has claimed
      // admin, suggestion-driven follow-ups default to unchecked (matches the
      // initial-mount rule `isAdmin: !hasAdmin`).
      this.isAdmin = !this.hasAdmin;
      // The form is on screen again — a fresh "viewed". Source stays that of
      // the flow which started this chain (creationSourceHook untouched).
      this.prefilledUsername = username || "";
      trackNewEmailCreationViewed({
        sourceHook: this.creationSourceHook,
        prefilledUsername: this.prefilledUsername || undefined,
      });
    },
    /**
     * Success-screen nudge → Create CTA. Re-prime the form with the
     * first queued username. The entry stays on the `pendingAccounts`
     * list until creation succeeds — `onMailboxCreated` removes it
     * then, so the rest of the queue survives a refresh in between.
     *
     * Empty list (the default-state CTA, clicked before queuing anything)
     * re-primes a blank form so "Create email account" opens a fresh
     * create instead of no-opping.
     */
    onNudgeCreate(usernames) {
      // With no license left the nudge still shows Create — that's the CTA when
      // buying isn't available for this partner/plan (see `shouldOfferBuy`).
      // There's nothing to create against, so close and let EmailAccounts run
      // its create gate, which shows the used-license screen.
      if (this.availableLicenses <= 0) {
        this.close();
        this.$emit("nudge-create-blocked");
        return;
      }
      const [next] = usernames || [];
      this.primeForNextCreate(next || "");
    },
    /**
     * Success-screen nudge → Buy CTA. Delegates to the shared
     * BuyEmailAccountsFlow mixin which handles the partner-vs-Titan
     * dispatch (deeplink for partner-billed, in-app /buy-email-account or
     * /billing-and-upgrade for Titan-billed).
     */
    onNudgeBuy(count) {
      this.close();
      this.triggerBuyFlow({ count: count || 1, sourceHook: "nudge_success" });
    },
    onNudgeReviewPending() {
      this.close();
      // Passed verbatim as the pending page's viewed-event source_hook.
      this.$router.push({
        name: "pending-accounts",
        query: { sourceHook: "mailbox creation success" },
      });
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    async createAccount() {
      // do nothing if the user is already creating an email to prevent multiple requests
      if (this.creatingEmail) return false;

      this.creatingEmail = true;

      if (this.altEmail && !REGX.email.test(this.altEmail)) {
        this.showAltEmailError = this.$t("emailAccounts.createEmail.invalidEmail");
        this.creatingEmail = false;
        return false;
      }

      if (this.user.length > 0) {
        this.email = this.user + "@" + smartStorage.getItem("domain");
        // Validate the composed address locally before any availability/create
        // call. Otherwise an invalid username surfaces a misleading error — the
        // Neo availability Lambda fails and reports "Network error", and Titan
        // leaks the backend's raw error payload.
        if (!REGX.email.test(this.email)) {
          this.serverError = this.$t("emailAccounts.createEmail.invalidEmail");
          this.creatingEmail = false;
          return false;
        }
        // Block duplicates client-side. The create endpoint returns success
        // for an already-existing address without creating a new account, so
        // without this guard the user gets a false "created" while the
        // existing mailbox's data is silently overwritten (MWDC-6284). The
        // address being replaced in a delete-and-create flow is excluded so
        // re-using it for the replacement still works.
        if (this.isDuplicateEmail(this.email)) {
          this.serverError = this.$t("emailAccounts.createEmail.emailAlreadyExists");
          this.creatingEmail = false;
          return false;
        }
        const alternateEmail = this.altEmail ? { alternateEmail: this.altEmail } : {};
        this.preloadImage();
        if (!(await this.checkEmailAvailability(this.email))) {
          this.creatingEmail = false;
          return;
        }

        // If emailToDeleteDetails.accountId is a number (i.e., a valid ID), it indicates that the mailbox with this ID should be deleted before a new one is created.
        if (this.emailToDeleteDetails?.accountId) {
          return emailService
            .deleteAndCreate({
              orderId: this.mailOrderId,
              bundleId: this.bundleId,
              oldAccountId: this.emailToDeleteDetails.accountId,
              email: this.email,
              password: this.password,
              ...alternateEmail,
            })
            .then(() => {
              // The delete-and-create pairing is one-shot: the old account is
              // already gone once this resolves, so any nudge-driven follow-up
              // create in this same modal session must fall through to the
              // plain createEmail path instead of re-issuing deleteAndCreate
              // against a stale (already-deleted) oldAccountId.
              this.$emit("delete-and-create-consumed");
              this.onCreateEmailSuccess();
              this.sendMailAccountDeletedEvent();
            })
            .catch(this.onCreateEmailError);
        }

        // If emailToDeleteDetails.accountId is false, no mailbox will be deleted when creating a new one.
        return emailService
          .createEmail({
            email: this.email,
            password: this.password,
            isAdmin: this.isAdmin,
            ...alternateEmail,
          })
          .then(this.onCreateEmailSuccess)
          .catch(this.onCreateEmailError);
      }
    },

    /**
     * Case-insensitive check of the composed address against the mailboxes
     * already created on this domain. The address currently being replaced
     * (delete-and-create flow) is excluded so re-using it is still allowed.
     */
    isDuplicateEmail(email) {
      const target = String(email).toLowerCase();
      const replacedEmail = String(this.emailToDeleteDetails?.emailId || "").toLowerCase();
      return (this.$store.getters.currentDomainMailboxDetails || []).some(account => {
        const existing = String(account?.email || "").toLowerCase();
        return existing && existing !== replacedEmail && existing === target;
      });
    },

    async checkEmailAvailability(email) {
      if (!isNeoBrand()) {
        return true;
      }
      const { isAvailable, errorCode, values } = await emailAvailabilityStatus(email);
      if (isAvailable) return true;
      this.serverError = this.$t(
        `emailAccounts.createEmail.emailAvailabilityErrors.${errorCode}`,
        values
      );
      this.emailUnavailabilityErrorMedusaEvent(email, errorCode);
      return false;
    },

    async emailUnavailabilityErrorMedusaEvent(email, errorCode) {
      if (errorCode !== EMAIL_VALIDITY_ERROR_CODES.TOP_BRAND) return;
      var eventObj = {
        eventName: "mail_account_creation_failed",
        email,
        error: "Brand name",
      };
      this.medusaEvents.send(
        this.medusaEvents.createEventObj(eventObj, this.domainName, "flock_order")
      );
    },

    sendMailAccountDeletedEvent() {
      if (this.emailToDeleteDetails?.accountId) {
        const eventObj = {
          eventName: "mail_account_deleted",
          /* the deleted mailbox travels as an attribute — the entity is the order.
             Its own account id, already guarded as present above. */
          account_id: this.emailToDeleteDetails.accountId,
          type: "pop",
          last_account: true,
        };
        this.medusaEvents.send(
          this.medusaEvents.createEventObj(eventObj, this.domainName, "flock_order")
        );
      }
    },

    onCreateEmailSuccess() {
      this.creatingEmail = false;
      this.success = true;
      clearLaunchEmailCreation();
      // Analytics snapshots BEFORE the pending row is removed and the
      // mailbox list refetches — the event describes the state at creation.
      const pendingMailboxCount = getPendingMailboxCount();
      const isFirstMailbox = (this.$store.getters.currentDomainMailboxDetails || []).length === 0;
      // Drop any matching row from the pending-accounts list — the
      // mailbox now exists, so the pending-accounts banner and the success-screen
      // pending preview shouldn't keep counting it. Go through the
      // service so the backend stays in sync with the Vuex
      // state (Vuex-only dispatch would resurrect the row on reload).
      pendingAccountsService
        .removePendingAccount(this.email)
        .catch(e => Logger.error("CreateEmail: removePendingAccount failed", e));
      this.$parent.getEmailList();
      eventBus.$emit("fetchCurrentPlan");
      eventBus.$emit("refetch-email-accounts");
      trackMailAccountCreated({
        email: this.email,
        type: "pop",
        sourceHook: this.creationSourceHook,
        firstAccountAutoCreated: this.emailToDeleteDetails?.accountId ? true : false,
        pendingMailboxCount,
        isFirstMailbox,
        // Whether the CREATED mailbox is an admin mailbox (the form checkbox).
        isAdmin: !!this.isAdmin,
      });
      // The success screen renders as a consequence of `success = true` above.
      trackSuccessViewed({
        sourceHook: this.creationSourceHook,
        isFirstMailbox,
        isAdmin: !!this.isAdmin,
        pendingMailboxCount,
      });

      if (this.altEmail) {
        this.medusaEvents.send(
          this.medusaEvents.createEventObj(
            {
              eventName: "account_atlernate_email_filled",
              /* the mailbox this event is about, as its own account id */
              account_id: this.accountIdByEmail(this.email),
              alternate_email: this.altEmail,
            },
            this.domainName,
            "flock_order"
          )
        );
      }
    },
    onCreateEmailError(response) {
      this.creatingEmail = false;
      const reasonCode = response?.data?.attrs?.unAvailabilityReason;
      if (reasonCode) {
        this.serverError = this.$t(
          `emailAccounts.createEmail.emailAvailabilityErrors.${reasonCode}`,
          {
            username: getUserNameFromEmail(this.email),
          }
        );
      }
      if (response && response.data && response.data.error) {
        this.serverError = this.serverError || response.data.error;
      }
      if (this.serverError) {
        clearLaunchEmailCreation();
      }
    },
    preloadImage() {
      // eslint-disable-next-line no-undef
      const logo = this.$options.filters.themeImage("email-creation-success");
      let img = new Image();
      img.onload = function () {
        console.log("Loaded::::");
      };
      img.onerror = err => {
        console.log("Failed to download", err);
      };
      img.src = logo;
    },
  },
  created() {
    this.hideBrandInfoInTooltip =
      viewTypes?.[document.documentElement.getAttribute("view")]?.["hideBrandInfoInTooltip"];
    this.storageLimit = this.currentPlanMailDetails.planDetails?.features?.length
      ? this.currentPlanMailDetails.planDetails?.features?.find(f => f.type === "storage").attrs
          .quota_in_mb
      : "";
    if (isSizeInGB(this.storageLimit)) {
      this.storageLimit = toGB(this.storageLimit);
      this.memorySizeUnit = "GB";
    }
    // Analytics: the create form is on screen (the dev success shortcut
    // mounts straight into the success view — no form view then).
    if (!this.success) {
      trackNewEmailCreationViewed({
        sourceHook: this.creationSourceHook,
        prefilledUsername: this.prefilledUsername || undefined,
      });
    }
  },
};
</script>
<template>
  <!--
    Two presentations: full-page modal-card in iframe view (matches every
    other widget-breadcrumb-nav user — CatchAllEmailModal, Disable2FA,
    ResetPassword, etc.), and inline page in standalone CP. EmailAccounts
    hides the listing while this is showing, so in both modes Create owns
    the content area.
  -->
  <!--
    Outer wrapper takes the `create-email-page-wrapper` class only in iframe.
    Unlike the sibling modals it is NOT a fixed-positioned overlay: the
    auto-resize-iframe contract (App.vue's ResizeObserver on #app posts the
    content height to the parent) can only measure in-flow content, so a
    fixed overlay collapses the reported height and clips the page behind an
    internal scrollbar (e.g. create-first-email loaded directly). Rendering
    in normal flow lets the page's natural height drive the iframe. App.vue's
    own breadcrumb/footer chrome is suppressed by EmailAccounts while this is
    showing (we render our own breadcrumb below). In standalone the outer
    wrapper is a transparent passthrough and the inner card renders inline.
  -->
  <div :class="{ 'create-email-page-wrapper': view.showModalAsPage }">
    <div
      :class="[
        'create-email',
        view.showModalAsPage ? 'modal-body-container modalPage' : 'create-email-inline',
      ]"
      role="region"
      aria-labelledby="createEmailHeading"
    >
      <div class="modal-content-wrapper">
        <!--
        Component-level breadcrumb is iframe-only. It's hidden only on the
        dead-end first-mailbox lead-in FORM — the auto-redirect that bounces a
        fresh CP straight into Create (`isUpfrontFirstLanding`), which has
        nothing to navigate back to and offers "Skip and go to control panel"
        instead. It's shown everywhere else: once that first mailbox is created
        the SUCCESS screen is navigable again (`success`); reaching the same
        first-create screen from Home (e.g. the Email accounts card) gives a
        Home to return to (`!isUpfrontFirstLanding`); and every non-first
        creation shows it on both form and success. App.vue's layout-level
        breadcrumb is suppressed by EmailAccounts while Create is showing, so
        this is the only breadcrumb in play. Standalone has no widget
        breadcrumb (showModalAsPage is false), so none renders there.
      -->
        <widget-breadcrumb-nav
          v-if="view.showModalAsPage && (success || !firstCreationActive || !isUpfrontFirstLanding)"
          modalName="create-new-email-account"
          :isModal="false"
          :showFooterGap="true"
          @onNavClick="onBreadcrumbNav"
        />
        <EmailCreatedSuccess
          :deletedEmail="emailToDeleteDetails?.accountId && deletedEmail ? deletedEmail : ''"
          :email="email"
          :password="password"
          :close="close"
          :isFirstMailbox="firstCreationActive"
          :isAdminMailbox="!!isAdmin"
          :creationSourceHook="creationSourceHook"
          v-if="success"
          @nudge-create="onNudgeCreate"
          @nudge-buy="onNudgeBuy"
          @nudge-review-pending="onNudgeReviewPending"
        />
        <div v-else>
          <!--
          Form header row: title on the left, optional Help link inline at
          the end. Help renders only on the form (not on the success view
          which has its own header chrome).
        -->
          <div class="create-email-header-row">
            <h4 class="create-email-header">
              {{ headerLabel }}
            </h4>
            <a
              v-if="helpUrl && !view.showModalAsPage"
              class="create-email-help"
              :href="helpUrl"
              target="_blank"
              rel="noopener"
            >
              <span class="create-email-help__icon" aria-hidden="true">?</span>
              {{ $t("emailAccounts.createEmail.help") }}
            </a>
          </div>

          <!-- email -->
          <div class="input-wrapper email-field">
            <label>{{ $t("emailAccounts.createEmail.emailLabel") }}</label>
            <div class="input-addon-wrapper">
              <input
                type="text"
                class="input"
                placeholder="e.g John"
                v-model.trim="user"
                @keyup="serverError = ''"
                autocomplete="new-password"
              />
              <span class="addon" :title="domainName">@{{ domainName }}</span>
            </div>
            <div class="error email-error">
              {{ serverError }}
            </div>
            <div v-if="hasAdmin">
              <span class="checkbox">
                <input
                  type="checkbox"
                  id="checkboxContent"
                  name="checkboxContent"
                  value="checkboxContent"
                  v-model="isAdmin"
                />
                <label for="checkboxContent">{{
                  $t("emailAccounts.createEmail.adminCheckbox")
                }}</label>
              </span>
              <i class="info" id="infoI" />
              <b-tooltip
                boundary="viewport"
                custom-class="custom-tooltip"
                target="infoI"
                placement="rightbottom"
              >
                <div
                  class="text-left"
                  v-html="
                    hideBrandInfoInTooltip
                      ? $t('emailAccounts.createEmail.widgetAdminInfo')
                      : $t('emailAccounts.createEmail.adminInfo', [brandDetails.name])
                  "
                />
              </b-tooltip>
            </div>
          </div>

          <!-- password -->
          <div class="input-wrapper">
            <label>{{ $t("emailAccounts.createEmail.passwordLabel") }}</label>
            <div class="input-addon-wrapper">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="input password-input"
                :class="{ 'has-error': showPasswordError }"
                v-bind:placeholder="$t('placeholders.password')"
                v-model="password"
                :readonly="autoGenerate"
                autocomplete="new-password"
              />
              <span
                :class="showPassword ? 'password-hidden' : 'password-shown'"
                @click="togglePassword"
                v-show="password.length > 0"
              ></span>
            </div>
            <div class="error" v-if="showPasswordError">
              {{ $t("emailAccounts.createEmail.passwordError") }}
            </div>
          </div>
          <generate-password :password="password" :view="view"></generate-password>
          <!-- alt email -->
          <div class="input-wrapper alt-email-field">
            <label>{{ $t("emailAccounts.createEmail.altEmailLabel") }}</label>
            <i class="info" id="infoAltEmail" />
            <b-tooltip
              boundary="viewport"
              custom-class="custom-tooltip"
              target="infoAltEmail"
              placement="rightbottom"
            >
              <div class="text-left">
                {{ $t("emailAccounts.createEmail.altEmailTooltip") }}
              </div>
            </b-tooltip>
            <div class="input-addon-wrapper">
              <input
                type="text"
                v-model.trim="altEmail"
                :placeholder="$t('emailAccounts.createEmail.altEmailPlacehoder')"
                @focus="showAltEmailError = ''"
                :class="{
                  'has-error': !!showAltEmailError,
                }"
              />
            </div>
            <div class="error" v-if="showAltEmailError">
              {{ showAltEmailError }}
            </div>
          </div>
          <p class="grey-font font-12px">
            {{ $t("emailAccounts.createEmail.share") }}
          </p>

          <!-- submit -->
          <div class="input-wrapper" :class="storageLimit ? 'btn-for-storage' : ''">
            <button
              class="button create-email-btn"
              :class="{
                invalid: isEmailCreationDisabled,
                processing: creatingEmail,
              }"
              @click="createAccount"
              :disabled="isEmailCreationDisabled"
            >
              {{ $t("emailAccounts.createEmail.createNew") }}
            </button>
            <button
              v-if="showCancelAction"
              type="button"
              class="button button__line-blue create-email-cancel-btn"
              @click="onCancelClick"
            >
              {{ $t("utility.cancel") }}
            </button>
            <button
              v-if="showSkipAction"
              type="button"
              class="btn-skip-first-mailbox"
              @click="skipFirstMailbox"
            >
              {{ $t("emailAccounts.createEmail.skipToControlPanel") }}
            </button>
          </div>
          <div
            class="input-wrapper storage-limit-option widget-storage-limit-info"
            v-if="storageLimit"
          >
            <img :src="'storage' | themeImage" />
            <p
              v-html="$t('emailAccounts.createEmail.storageLimit', [storageLimit, memorySizeUnit])"
              class=""
            ></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.modal-overlay {
  position: fixed;
  top: var(--modalOverlayTop);
  bottom: 0;
  left: 0;
  right: 0;
  background-color: var(--boxShadowCol5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
}

// Inline rendering of the Create email page (replaces the old modal-overlay
// presentation). Same look in both iframe and standalone: a white surface
// card sitting inside the page content area — no fixed positioning, no
// overlay, no constrained modal width.
.create-email-inline {
  background: var(--tabBgCol);
  border-radius: 4px;
  // Border around the white create window — also wraps the success screen,
  // which renders inside this same card (MWDC-6349).
  border: 1px solid var(--lineSepaCol);
  padding: 20px 20px 0px;
  width: 100%;
  height: auto;
  box-shadow: none;
  // Reset properties that .modal-body-container historically set when these
  // contents were a modal card.
  max-width: none;
  min-width: 0;
  max-height: none;
  overflow: visible;

  .create-email-header {
    font-size: 26px;
    line-height: 32px;
    font-weight: 700;
    color: var(--primaryTextCol);
  }

  // Form fields run wider on the inline page than they did in the old
  // modal — but capped so very wide screens don't stretch them.
  .input-addon-wrapper {
    width: 100%;
    max-width: 560px;
  }
  .alt-email-field input {
    max-width: 560px;
  }

  // Storage footer: thin separator + folder-icon + copy, anchored at the
  // bottom of the form block (not the viewport).
  .storage-limit-option {
    position: static;
    border: none;
    border-top: 1px solid var(--lineSepaCol);
    padding: 18px 0 0 0;
    margin-top: 32px;
    width: 100%;
    p {
      color: var(--secondaryTextCol);
    }
  }
}

// Header row aligns the title and the inline Help link.
.create-email-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

// Help link sits inline at the end of the header row (no absolute pos).
// `!important` on text-decoration: unscoped global rules from other
// components (e.g. `a:hover { text-decoration: underline }`) win the
// cascade otherwise and paint an underline across the grey badge.
.create-email-help {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--secondaryTextCol);
  text-decoration: none !important;
  font-size: 14px;

  &:hover,
  &:focus {
    text-decoration: none !important;
    color: var(--linkBtnCol);
  }
  &:focus {
    outline: 2px solid var(--linkBtnCol);
    outline-offset: 2px;
  }
  // Grey circle badge with a white "?" inside — built inline so we don't
  // need a dedicated SVG asset.
  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--secondaryTextCol);
    color: var(--tabBgCol);
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    text-decoration: none !important;
  }
}

.create-email {
  .checkbox {
    // $params are in the given order
    // margin-top , margin-right, margin-bottom, margin-left
    // padding-top , padding-right, padding-bottom, padding-left
    //width , height
    @include custom-checkbox(8, 12, 0, 0, 2, 2, 2, 2, 16, 16);
    label {
      font-weight: normal;
    }
  }
  .checkbox + i.info {
    vertical-align: 0;
    transform: translateY(-1px);
  }
  .note {
    border-radius: 5px;
    border: solid 1px var(--noteBordCol);
    padding: 16px 16px 16px 40px;
    box-sizing: border-box;
    margin: 32px 0;
    font-size: 14px;
    font-weight: normal;
    color: var(--primaryTextCol);
    background-color: var(--warningBgCol);
    background-image: url("../../assets/error-state-2.svg");
    background-position: 12px 14px;
    background-repeat: no-repeat;
    width: auto;
    p {
      margin-bottom: 12px;
      color: var(--genericTextCol);
    }

    a {
      width: auto;
      color: var(--linkBtnCol);
    }
  }
  .widget-note {
    margin: -20px 0 24px 0;
  }

  .create-email-btn {
    transition:
      padding-left,
      background-position-x 0.3s ease-in-out;
    &.processing {
      background-image: url("../../assets/loading-spinner-white.svg");
      background-repeat: no-repeat;
      background-position-x: 10px;
      background-position-y: center;
      background-size: 25px;
      padding-left: 45px;
    }
  }
}

.alt-email-field {
  margin-bottom: 4px;
  input {
    width: 100%;
    border-radius: 4px;
    border: solid 1px var(--inputBorderCol);
    background-color: var(--tabBgCol);
    display: block;
    padding: 8px 15px;
    margin: 0 auto;

    &:hover,
    &:focus,
    &:active {
      outline: none;
    }

    &.has-error {
      border-color: var(--errorPrimaryTextCol);
    }

    &:focus {
      border-color: var(--inputFocusBorderCol);
    }
  }
}

i.info {
  background-color: var(--tabBgCol);
  display: inline-block;
  width: 12px;
  height: 20px; // ideally it should be 12 but just to adjust carrot position keeping 20
  background: url("../../assets/info-i.svg") no-repeat bottom;
  background-size: contain;
  vertical-align: -2px;
  margin-left: 5px;
}

.email-field {
  label {
    display: inline-block;
    margin-top: 6px;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    line-height: 1.29;
    color: var(--primaryTextCol);
    input {
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-right: 2px;
      vertical-align: -1px;
    }
  }
}
.input-addon-wrapper {
  width: var(--inputBoxWidth);
  input {
    &:focus {
      border: 1px solid var(--inputFocusBorderCol);
    }
  }
  .password-input {
    padding-right: 15px; // increasing padding for placeholder
  }
}

.custom-tooltip {
  color: var(--tabBgCol);

  & > div {
    padding: 16px;
    font-size: 14px;
    font-weight: normal;
  }
}

.btn-for-storage {
  margin-top: 32px;
  button {
    background-color: var(--secondaryBtnCol);
    font-size: var(--modalBtnTextSize);
  }
  .btn-close {
    font-size: 28px;
  }

  .btn-skip-first-mailbox {
    // Secondary action on the iFrame first-mailbox Create screen.
    background: transparent;
    border: none;
    margin-left: 24px;
    padding: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--linkBtnCol);
    cursor: pointer;
    &:focus {
      outline: 2px solid var(--linkBtnCol);
      outline-offset: 2px;
    }
  }
}
.storage-limit-option {
  position: absolute;
  bottom: 0;
  padding: 18px 40px 4px;
  border: 1px solid var(--textSepaCol);
  left: 0;
  width: 100%;
  p {
    display: inline-block;
    margin-left: 7px;
    font-size: 12px;
  }
  img {
    width: 18px;
    height: 18px;
  }
}

.widget-storage-limit-info {
  position: relative !important;
  border: none;
  border-top: 1px solid var(--lineSepaCol);
  top: -9px;
  padding-left: 0;
}

.checkboxes label {
  font-weight: normal;
  margin-left: 5px;
  margin-right: 20px;
}

input[type="radio"] {
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
}

.modal-body-container {
  background: var(--tabBgCol);
  box-shadow: 2px 2px 20px 1px;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  max-width: 600px;
  min-width: 500px;
  padding: 40px;
  border-radius: 4px;
  height: auto;
  max-height: 700px;
  position: relative;

  .modal-content-wrapper {
    color: var(--primaryTextCol);
    h4 {
      font-size: 20px;
      margin-bottom: 8px;
      font-weight: 600;
    }

    & > .color-grey {
      margin-bottom: 22px;
    }
  }
}

.modalPage {
  padding-left: 0 !important;
  padding-top: 0;
  min-width: 100% !important;
  min-height: 100% !important;
  background: var(--primaryBgCol) !important;
  height: 100%;
  overflow: auto;
  border-radius: 0px;
}

// Iframe full-page presentation: the modalPage class makes the card take
// the whole viewport, but the form/success content inside still needs a
// readable content column. Cap it at 672px — same as the SuccessScreenNudge
// row column (560px input + 12px gap + 100px button) — so the success
// header buttons, credential rows, and per-row Add buttons all line up
// vertically instead of hugging the right edge of the viewport.
// Iframe full-page wrapper: a plain in-flow block (no fixed overlay) so the
// #app ResizeObserver in App.vue measures the page's real height. See the
// template comment on `create-email-page-wrapper`.
.create-email-page-wrapper {
  width: 100%;
}

.create-email.modal-body-container.modalPage {
  align-items: flex-start;
  // Grow with content in normal flow — drop the floating-card chrome and the
  // 700px cap / internal scroll that .modal-body-container sets, so tall forms
  // (e.g. the success/nudge screens) extend the page instead of clipping.
  // `!important` is needed to beat `.modalPage { min-height: 100% !important }`,
  // which otherwise stretches the card to the full viewport and leaves a big
  // blank gap under shorter content like the pending-accounts nudge (MWDC-6277).
  min-height: auto !important;
  max-height: none;
  height: auto;
  overflow: visible;
  box-shadow: none;
  padding: 0;

  .modal-content-wrapper {
    width: 100%;
    max-width: 672px;
    padding: 0px 24px;
  }

  // Match the inline page's input width cap so the form inputs don't span
  // the full content column.
  .input-addon-wrapper {
    width: 100%;
    max-width: 560px;
  }
  .alt-email-field input {
    max-width: 560px;
  }
}
.remove-padding-left {
  padding-left: 0;
}

.leftAlignItems {
  text-align: left !important;
}
.text-left {
  color: inherit;
}
.primaryBgColorClass {
  background-color: var(--primaryBgCol) !important;
}

.modal-note {
  color: var(--secondaryTextCol);
  font-size: 12px;
  line-height: 16px;
}

.password-shown,
.password-hidden {
  display: inline-block;
  width: 20px;
  height: 20px;
  vertical-align: middle;
  position: relative;
  bottom: 2px;
  margin: 0 8px;
  cursor: pointer;
}

.password-shown {
  background: url("../../assets/password-toggle-on.svg") no-repeat center / contain;
}

.password-hidden {
  background: url("../../assets/password-toggle-off.svg") no-repeat center / contain;
}

.modal-header,
.modal-footer {
  padding: 15px;
  display: flex;
}

.modal-header {
  border-bottom: 1px solid var(--readOnlyTxtBgCol);
  color: var(--modalHeadCol);
  justify-content: space-between;
}

.modal-footer {
  border-top: 1px solid var(--readOnlyTxtBgCol);
  justify-content: flex-end;
}

.modal-body {
  position: relative;
  padding: 20px 10px;
}

.btn-close {
  border: none;
  font-size: 28px;
  cursor: pointer;
  font-weight: bold;
  color: grey;
  background: transparent;
  position: absolute;
  z-index: 1;
  top: 15px;
  right: 15px;
  line-height: 15px;
  padding: 0;
  background: none !important;

  &:focus {
    outline: 0;
    border: 0;
  }
}
.cancel-btn {
  position: static;
  font-size: 14px !important;
  margin-left: 24px;
  color: var(--primaryTextCol);
}
.create-email-cancel-btn {
  // Standalone-only secondary action sitting next to "Create new account".
  margin-left: 12px;
}

.success-banner {
  background: var(--tabBgCol);
  .header {
    margin-bottom: 22px;
    display: flex;
  }
  h5 {
    word-break: break-all;
  }

  img.block-image {
    margin-bottom: 13px;
  }

  .password-actions {
    position: relative;
    display: inline-block;
    user-select: none;

    .password-text {
      cursor: pointer;
    }

    .password-shown,
    .password-hidden {
      width: 15px;
      height: 15px;
    }

    p {
      color: var(--primaryTextCol);
      position: relative;
      margin-bottom: 4px;
    }
  }
  .send-details {
    width: 450px;
    word-break: break-word;
  }
}
.email-created {
  font-size: 20px;
  padding-left: 19px;
  word-break: break-word;
}
.email-creation-success-header {
  font-size: 14px;
}

.email-info-container {
  background: var(--tertiaryBgCol);
  border: 1px solid var(--lineSepaCol);
  border-radius: 2px;
  padding: 30px 24px;
  max-width: 500px;
  strong {
    padding-bottom: 18px;
    font-weight: 500;
    display: inline-block;
  }
  .span-link {
    text-decoration: underline;
    cursor: pointer;
    color: var(--tertiaryBtnCol);
  }
  .url,
  .email {
    margin-bottom: 9px;
    word-break: break-all;
  }
  .password {
    padding-bottom: 21px;
  }
}
.modal-page-success-banner {
  text-align: left;
  background-color: var(--primaryBgCol);
}
.margin-gap {
  height: 30px;
}
.doneBtn {
  padding: 10px 53px;
}
.infoEndBorder {
  border: 1px solid var(--lineSepaCol);
  margin-bottom: 29px;
}
.sendDetails {
  font-weight: 500;
  word-break: break-all;
}

.send-details {
  width: var(--inputBoxWidth);
  position: relative;

  .input {
    padding-right: 50px;

    &:focus {
      border: 1px solid var(--inputFocusBorderCol);
    }
  }
}

.addon-span {
  color: var(--primaryBtnCol);
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 500;
  cursor: pointer;

  &.invalid {
    opacity: 0.5;
    cursor: default;
  }
}

/*toggle switch css*/
.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 14px;
  margin-right: 10px;
  top: 3px;
}
.marginFromLeft {
  margin-left: 5px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--sliderBgCol);
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: -3px;
  bottom: -1px;
  background-color: var(--tabBgCol);
  -webkit-transition: 0.4s;
  transition: 0.4s;
  box-shadow: 0 2px 6px 1px var(--boxShadowCol1);
}

input:checked + .slider {
  background-color: var(--primaryBtnCol);
}

/*input:focus + .slider {*/
/*box-shadow: 0 0 1px #2196F3;*/
/*}*/

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.email-error {
  display: flex;
  max-width: 430px;
}
</style>
